import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { AppService } from '@services/app.service';
import { pick } from 'lodash';
import { catchError, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class LoggingInterceptor implements HttpInterceptor {
  private appService = inject(AppService);

  intercept(request: HttpRequest<object>, next: HttpHandler): Observable<HttpEvent<object>> {
    console.debug('HTTP request', pick(request, ['method', 'url', 'body']));
    return next.handle(request).pipe(
      catchError(async (error: HttpErrorResponse) => {
        try {
          await this.appService.checkVersion();
        } catch {
          // Ignore version-check failures and preserve the original HTTP error.
        }
        this.reportToSentry(error);
        throw error;
      }),
    );
  }

  private reportToSentry(error: HttpErrorResponse): void {
    if (error.status === 0) {
      // Network failure (offline, CORS, request cancelled) — not actionable; record as breadcrumb only.
      Sentry.addBreadcrumb({ category: 'http', level: 'warning', message: `Network error: ${error.url}` });
      return;
    }
    if (error.status >= 400 && error.status < 500) {
      // Client errors (validation, auth, not found, etc.) — expected/actionable client-side, not a server bug; record as breadcrumb only.
      Sentry.addBreadcrumb({ category: 'http', level: 'warning', message: `Client error ${error.status}: ${error.url}` });
      return;
    }
    Sentry.withScope(scope => {
      scope.setLevel('error');
      scope.setExtra('status', error.status);
      scope.setExtra('url', error.url);
      scope.captureException(new Error(error.message));
    });
  }
}
